import React, { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import IntegrationHero from '../../components/integration-hero'
import Breadcrumbs from '../../components/breadcrumbs'
import Footer from '../../components/footer'
import { getCanonicalUrl } from '../../utils/canonical'
import DarkNav from '../../components/dark-nav'
import './email-to-sms.css'

const EmailToSmsIntegration = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    // Initialize AOS animations if available
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      })
    }

    // Scroll to top functionality
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      
      if (typeof AOS !== 'undefined') {
        AOS.refresh()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const trackEvent = (eventName, properties = {}) => {
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, properties)
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Email to SMS' }
  ]

  const heroContent = {
    title: "Email to SMS with DataFlows",
    subtitle: "Send SMS from Outlook & Gmail",
    description: "Compose an email and have it delivered as an SMS. Fast setup with authenticated sender emails and flexible sender ID control. Perfect for customer support teams and automated systems.",
    primaryCta: {
      text: "Enable Email to SMS",
      href: "https://sms.dataflows.com.au/register",
      onClick: () => trackEvent('email_to_sms_setup_started')
    },
    secondaryCta: {
      text: "Contact Sales",
      href: "/contact-us"
    }
  }

  const features = [
    {
      icon: "🔐",
      title: "No Connection Needed",
      description: "Send from Gmail, Outlook, Apple Mail or your phone. Nothing connects to or reads your mailbox: you simply email the mobile number at smsmail.dataflows.com.au."
    },
    {
      icon: "🏷️",
      title: "Sender ID Control",
      description: "Select and validate sender IDs for consistent branding. Choose from alphanumeric IDs or dedicated numbers based on your region and carrier requirements."
    },
    {
      icon: "🧾",
      title: "Smart Message Handling",
      description: "The email body becomes the SMS. Long messages are split into multiple SMS parts and use your normal credits, so keep the body short."
    },
    {
      icon: "📊",
      title: "Delivery Tracking",
      description: "Real-time delivery receipts and retry logic for failed deliveries. Comprehensive logs with searchable message history and CSV export for reporting."
    }
  ]

  const useCases = [
    {
      title: "Customer Support Teams",
      description: "Reply to customers via email threads and have responses delivered as SMS for urgent matters",
      icon: "↔️"
    },
    {
      title: "Automated Notifications", 
      description: "Convert existing email alerts (order confirmations, shipping updates) into SMS for better visibility",
      icon: "📬"
    },
    {
      title: "Marketing Campaigns",
      description: "Repurpose email marketing content for SMS campaigns with higher open rates",
      icon: "📢"
    },
    {
      title: "System Monitoring",
      description: "Turn server alerts and monitoring notifications into immediate SMS alerts to on-call staff",
      icon: "🚨"
    }
  ]

  const quickStartSteps = [
    {
      step: 1,
      title: "Authorize Your Address",
      description: "Send from your DataFlows login email, or add another address on the Email to SMS page in your account",
      cta: "Get Started"
    },
    {
      step: 2,
      title: "Configure Sender ID",
      description: "Choose the sender ID or number used for email sends in Default SMS Settings"
    },
    {
      step: 3,
      title: "Send First SMS",
      description: "Email the mobile number at smsmail.dataflows.com.au, for example 61404235133@smsmail.dataflows.com.au"
    },
    {
      step: 4,
      title: "Monitor Results",
      description: "Track delivery status and costs in your DataFlows dashboard"
    }
  ]

  const faqItems = [
    {
      question: "Which email apps work?",
      answer: "Any email app: Gmail, Outlook, Apple Mail or your phone. There is no plugin or connection to set up. The email has to come from your DataFlows login email or an address you've authorized, and your domain has to pass DKIM or DMARC, which Gmail, Outlook and most business email do automatically."
    },
    {
      question: "How does the email to SMS gateway work?",
      answer: "You email the mobile number at smsmail.dataflows.com.au (for example 61404235133@smsmail.dataflows.com.au) and the email body is sent as an SMS from your own sender ID or number, using your plan credits. Only your login email and the addresses you authorize can send."
    },
    {
      question: "Can I use my company's sender ID?",
      answer: "Yes! You can configure custom alphanumeric sender IDs (where regulations permit) or use dedicated virtual numbers to maintain your brand identity in SMS messages."
    },
    {
      question: "What happens if my email is too long for SMS?",
      answer: "Long messages are split into multiple SMS parts, and each part uses credits, so keep the body short. Turn off automatic signatures such as \"Sent from Outlook for Android\" when you send to an SMS address, because they become part of the SMS."
    },
    {
      question: "How is it kept secure?",
      answer: "Only your DataFlows login email and the addresses you authorize can send, and the email has to pass DKIM or DMARC authentication; emails that fail are not sent. Nothing connects to or reads your mailbox, and no email password is ever shared."
    }
  ]

  const workflowSteps = [
    {
      title: "Authorize Your Address",
      description: "Use your DataFlows login email, or add other addresses on the Email to SMS page. No mailbox connection needed.",
      icon: "🔐"
    },
    {
      title: "Configure Settings",
      description: "Choose your sender ID and set up email address authorization in DataFlows dashboard.",
      icon: "⚙️"
    },
    {
      title: "Compose & Send",
      description: "Write your email to phonenumber@smsmail.dataflows.com.au and it's delivered as SMS.",
      icon: "📧"
    },
    {
      title: "Track Delivery",
      description: "Monitor delivery status, costs, and responses in real-time through your dashboard.",
      icon: "📊"
    }
  ]

  return (
    <div className="email-sms-container">
      <DarkNav />
      <Helmet>
        <title>Email to SMS with DataFlows — Send SMS from Outlook & Gmail</title>
        <meta
          name="description"
          content="Use DataFlows Email to SMS to send SMS directly from Outlook and Gmail. Fast setup, sender ID control, and reliable delivery."
        />
        <meta name="keywords" content="email to SMS, Outlook SMS, Gmail SMS, email integration, automated SMS, business messaging" />
        <meta property="og:title" content="Email to SMS Integration - DataFlows" />
        <meta property="og:description" content="Send SMS directly from Outlook and Gmail with DataFlows Email to SMS integration. Perfect for support teams and automated notifications." />
        <meta property="og:image" content="https://dataflows.com.au/images/integrations/email-sms/email%20to%20sms%20UI%20setup%20.png" />
        <link rel="canonical" href={getCanonicalUrl('/integrations/email-to-sms')} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
            { "@type": "ListItem", "position": 2, "name": "Integrations", "item": "https://dataflows.com.au/integrations" },
            { "@type": "ListItem", "position": 3, "name": "Email to SMS", "item": "https://dataflows.com.au/integrations/email-to-sms" }
          ]
        })}</script>
      </Helmet>

      {/* Breadcrumbs */}
      <div className="email-sms-breadcrumbs">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Hero Section */}
      <IntegrationHero {...heroContent} />

      {/* Setup Preview */}
      <section className="email-sms-preview" data-aos="fade-up">
        <div className="container">
          <div className="preview-content">
            <div className="preview-text" data-aos="fade-right">
              <h2 className="section-title">Simple Setup, Powerful Results</h2>
              <p className="section-description">
                Configure Email to SMS in minutes with our intuitive interface. 
                Choose your sender identity, authorize the addresses that can send, and start
                sending SMS messages from any email app, with no plugin or connection.
              </p>
              <div className="preview-features">
                <div className="preview-feature">
                  <svg className="feature-check" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" fill="currentColor"/>
                  </svg>
                  <span>No plugin or mailbox connection</span>
                </div>
                <div className="preview-feature">
                  <svg className="feature-check" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" fill="currentColor"/>
                  </svg>
                  <span>Custom sender ID selection</span>
                </div>
                <div className="preview-feature">
                  <svg className="feature-check" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" fill="currentColor"/>
                  </svg>
                  <span>Real-time delivery tracking</span>
                </div>
              </div>
            </div>
            <div className="preview-image" data-aos="fade-left">
              <img
                src="/images/integrations/email-sms/email to sms UI setup .png"
                alt="Email to SMS setup interface showing sender ID configuration and authorized email addresses"
                loading="lazy"
                className="setup-screenshot"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="email-sms-workflow" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How Email to SMS Works</h2>
            <p className="section-description">
              Transform your email workflow into SMS communication in 4 simple steps
            </p>
          </div>
          
          <div className="workflow-grid">
            {workflowSteps.map((step, index) => (
              <div key={index} className="workflow-step" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="step-icon">{step.icon}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
                {index < workflowSteps.length - 1 && (
                  <svg className="workflow-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="email-sms-features" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Powerful Email to SMS Features</h2>
            <p className="section-description">
              Everything you need to bridge email and SMS communication effectively
            </p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div 
                  className="feature-icon emoji-icon"
                  style={{
                    fontSize: '48px',
                    width: '64px',
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {feature.icon}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="email-sms-use-cases" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Perfect for Every Business Need</h2>
            <p className="section-description">
              From customer support to automated notifications, Email to SMS adapts to your workflow
            </p>
          </div>
          
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div 
                  className="use-case-icon emoji-icon"
                  style={{
                    fontSize: '48px',
                    width: '64px',
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {useCase.icon}
                </div>
                <h3 className="use-case-title">{useCase.title}</h3>
                <p className="use-case-description">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Start Section */}
      <section className="email-sms-quick-start" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Get Started in Minutes</h2>
            <p className="section-description">
              Set up Email to SMS integration with your existing email client quickly and securely
            </p>
          </div>
          
          <div className="steps-grid">
            {quickStartSteps.map((step, index) => (
              <div key={step.step} className="step-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="step-number">{step.step}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
                {step.cta && (
                  <a
                    href="https://sms.dataflows.com.au/register"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="step-cta"
                    onClick={() => trackEvent('email_to_sms_setup_started', { source: 'quick_start' })}
                  >
                    {step.cta}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Summary */}
      <section className="email-sms-pricing" data-aos="fade-up">
        <div className="container">
          <div className="pricing-content">
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <div className="pricing-grid">
              <div className="pricing-item">
                <div className="price">3.5¢ AUD</div>
                <div className="price-label">Per Outbound SMS</div>
              </div>
              <div className="pricing-item">
                <div className="price">Free</div>
                <div className="price-label">Setup & Configuration</div>
              </div>
              <div className="pricing-item">
                <div className="price">No Contracts</div>
                <div className="price-label">Pay-as-you-go Credits</div>
              </div>
            </div>
            <div className="pricing-cta">
              <a
                href="https://sms.dataflows.com.au/register"
                target="_blank"
                rel="noreferrer noopener"
                className="cta-button primary"
                onClick={() => trackEvent('signup_cta_clicked', { source: 'email-sms-pricing' })}
              >
                Start Free Trial
              </a>
              <a
                href="/contact-us"
                className="cta-button secondary"
                onClick={() => trackEvent('contact_support_clicked', { integration: 'email-to-sms' })}
              >
                Enterprise Pricing
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="email-sms-faq" data-aos="fade-up">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            {faqItems.map((item, index) => (
              <div key={index} className="faq-item" data-aos="fade-up" data-aos-delay={index * 100}>
                <h3 className="faq-question">{item.question}</h3>
                <p className="faq-answer">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="email-sms-final-cta">
        <div className="container">
          <div className="cta-content" data-aos="fade-up">
            <h2 className="cta-title">Ready to Bridge Email and SMS?</h2>
            <p className="cta-description">
              Join businesses using DataFlows Email to SMS to reach customers instantly from their existing email workflow
            </p>
            <div className="cta-buttons">
              <a
                href="https://sms.dataflows.com.au/register"
                target="_blank"
                rel="noreferrer noopener"
                className="cta-button primary"
                onClick={() => trackEvent('email_to_sms_setup_started', { source: 'final-cta' })}
              >
                Enable Email to SMS
              </a>
              <a
                href="/contact-us"
                className="cta-button secondary"
                onClick={() => trackEvent('contact_support_clicked', { source: 'final-cta' })}
              >
                Contact Sales
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer will be inherited from main layout */}
      <Footer />

      {/* Scroll to Top Button */}
      <div 
        className={`scroll-to-top ${showScrollTop ? 'show' : ''}`} 
        onClick={scrollToTop}
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 5L19 12H16V19H8V12H5L12 5Z" fill="currentColor"/>
        </svg>
      </div>
    </div>
  )
}

export default EmailToSmsIntegration